"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Input } from "@/components/ui/input";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CallToAction() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.3
  });
  return <>
      <section ref={ref} className="relative min-h-[30vh] flex items-center justify-center bg-dark-background text-foreground-dark px-6 py-16" style={{
      backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(255,255,255,0.02) 2px, rgba(255,255,255,0.02) 4px)`
    }}>
        <div className="max-w-3xl mx-auto text-center space-y-6">
          <h2 className={`text-3xl md:text-4xl font-display font-bold transition-all duration-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
            Ready to Revive Your Ride?
          </h2>
          <p className={`text-lg text-foreground-dark/90 transition-all duration-200 delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
            Book your appointment today and let our expert technicians restore
            your motorcycle to its former glory.
          </p>
          <Button onClick={() => setIsModalOpen(true)} className="btn-primary mt-6 transition-transform duration-[80ms] ease-out hover:scale-[1.02]" size="lg">
            Schedule Appointment
          </Button>
        </div>
      </section>

      <Sheet open={isModalOpen} onOpenChange={setIsModalOpen}>
        <SheetContent side="bottom" className="h-auto max-h-[90vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Book Your Appointment</SheetTitle>
            <SheetDescription>
              Fill out the form below and we'll get back to you shortly.
            </SheetDescription>
          </SheetHeader>

          <FormProvider formId="appointment-form-cta" sectionName="call-to-action" defaultValues={{
          name: "",
          email: "",
          phone: "",
          service: "",
          message: ""
        }} validationRules={{
          name: {
            required: true,
            minLength: 2,
            maxLength: 50
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true,
            pattern: "phone"
          },
          service: {
            required: true
          },
          message: {
            required: false
          }
        }} submitText="Book Appointment" successMessage="Thank you! We'll contact you soon to confirm your appointment." className="mt-6">
            <AppointmentForm onSuccess={() => setIsModalOpen(false)} />
          </FormProvider>
        </SheetContent>
      </Sheet>
    </>;
}
function AppointmentForm({
  onSuccess
}: {
  onSuccess: () => void;
}) {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-6 bg-green-50 border border-green-200 rounded-lg space-y-4">
        <p className="text-green-800">{successMessage}</p>
        <Button onClick={() => {
        resetForm();
        onSuccess();
      }} variant="outline">
          Close
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-4" data-index={0}>
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[\d\s\-\+\(\)]+$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 123-4567" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="service" rules={{
        required: "Please select a service"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Service Needed</FormLabel>
              <FormControl>
                <select {...field} className="w-full h-9 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
                  <option value="">Select a service...</option>
                  <option value="panel-repaint">Motorcycle Panel Repaint</option>
                  <option value="crash-repair">Crash Repair & Structural Reinforcement</option>
                  <option value="frame-restoration">Frame Restoration</option>
                  <option value="dent-removal">Dent Removal</option>
                  <option value="paint-touchup">Paint Touch-Ups</option>
                  <option value="custom-graphics">Custom Graphics & Decals</option>
                  <option value="other">Other</option>
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" render={({
        field
      }) => <FormItem>
              <FormLabel>Additional Details (Optional)</FormLabel>
              <FormControl>
                <textarea {...field} placeholder="Tell us more about your motorcycle and what needs fixing..." className="w-full min-h-[100px] rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full">
          {isSubmitting ? <>
              <span className="animate-spin mr-2">⏳</span>Sending...
            </> : "Book Appointment"}
        </Button>
      </div>
    </Form>;
}